import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { isEqual, noop, get } from "lodash";
import { pluralize } from "@tvg/utils/generalUtils";
import { RaceStatusEnum } from "@tvg/ts-types/Race";
import { isRaceOff } from "@tvg/sh-lib-my-bets/utils/raceDetails";
import { AlertInline, Header, useTheme } from "@tvg/design-system";

import {
  getMyBetsEmptyStateMessage,
  getProgramPageMessages
} from "../../../store/selectors";
import useCurrentRaceWagers from "../../../hooks/useCurrentRaceWagers";
import { MyBetsProps } from "./types";
import { getBetValue, getTotalWinAmount } from "./helpers";
import BetInfo from "./components/BetInfo";
import MyBetsEmptyStateCard from "./components/MyBetsEmptyState";
import BetTotal from "./components/BetTotal";
import {
  MyBetsContainer,
  StyledHeaderSection,
  BetsSummary,
  BetInfoContainer
} from "./styled-components";

export const MyBetsCard = React.forwardRef<HTMLDivElement, MyBetsProps>(
  (
    {
      qaLabel = "race-bet",
      isLogged,
      race,
      races,
      behgClient,
      onRepeatWager,
      isAuxiliaryHigherThanPrimary,
      myBetsMaxHeight
    },
    ref
  ) => {
    const mybetsEmptyMessage = useSelector(getMyBetsEmptyStateMessage);
    const [totalWinAmount, setTotalWinAmount] = useState(0);
    const theme = useTheme();
    const headerRef = useRef<HTMLDivElement>(null);
    const areRunnersNumGreaterThan2 =
      race.bettingInterests && race.bettingInterests?.length > 2;
    const headerContainerHeight =
      headerRef.current?.getBoundingClientRect().height;

    const maxHeight =
      headerContainerHeight &&
      myBetsMaxHeight &&
      myBetsMaxHeight - headerContainerHeight;

    const { trackCode, raceNumber, raceWagersSummary, raceWagers } =
      useCurrentRaceWagers({
        race,
        behgClient
      });

    const programPageMessages = useSelector(getProgramPageMessages);

    useEffect(() => {
      if (isRaceOff(race.status?.code || RaceStatusEnum.OPEN)) {
        const winAmount = getTotalWinAmount(raceWagers);
        setTotalWinAmount(+winAmount);
      }
    }, [raceWagers, race.status?.code]);

    const betValue = getBetValue(trackCode, raceNumber, race.raceDate || "");

    if (!isLogged) return null;

    const areWagersNumberGreaterThan3 = raceWagers.length >= 3;

    return (
      <MyBetsContainer ref={ref} data-qa-label={qaLabel} theme={theme}>
        {raceWagers.length === 0 ? (
          <MyBetsEmptyStateCard
            qaLabel={`${qaLabel}-empty-card`}
            title={mybetsEmptyMessage.title}
            message={mybetsEmptyMessage.body}
          />
        ) : (
          <>
            <StyledHeaderSection
              ref={headerRef}
              data-qa-label={`${qaLabel}-header-section`}
              theme={theme}
            >
              <Header qaLabel={`${qaLabel}-header`} tag="h3" fontFamily="bold">
                {`R${race.raceNumber} Bets`}
              </Header>
              <BetsSummary>
                <BetTotal
                  label={pluralize(
                    raceWagersSummary.totalGambledCount,
                    "bet",
                    false
                  )}
                  value={raceWagersSummary.totalGambledCount}
                  qaLabel={`${qaLabel}-bets-counter`}
                />
                <BetTotal
                  label="staked"
                  value={raceWagersSummary.totalGambledAmount}
                  qaLabel={`${qaLabel}-bets-amount`}
                  isCurrency
                />
              </BetsSummary>
            </StyledHeaderSection>
            {totalWinAmount > 0 && (
              <AlertInline
                message={get(
                  programPageMessages,
                  "betList.winningsMessage",
                  ""
                ).replace("$X", `$${totalWinAmount.toFixed(2)}`)}
                variant="success"
                qaLabel="alert-inline"
                type="fixed"
                icon="cup"
              />
            )}
            <BetInfoContainer
              isAuxiliaryHigherThanPrimary={isAuxiliaryHigherThanPrimary}
              myBetsMaxHeight={maxHeight}
              areRunnersNumGreaterThan2={areRunnersNumGreaterThan2}
              areWagersNumberGreaterThan3={areWagersNumberGreaterThan3}
            >
              {raceWagers.map((wager, wagerIndex) => (
                <BetInfo
                  key={`bet-${wager.serialNumber}`}
                  mt={wagerIndex ? "0" : "space-4"}
                  race={race}
                  races={races}
                  wager={wager}
                  betValue={betValue}
                  onRepeatWager={onRepeatWager}
                  onCancelWager={noop}
                  qaLabel={`${qaLabel}-cell-${wagerIndex}`}
                />
              ))}
            </BetInfoContainer>
          </>
        )}
      </MyBetsContainer>
    );
  }
);

export default React.memo(
  MyBetsCard,
  (prevProps, nextProps) =>
    prevProps.isLogged === nextProps.isLogged &&
    prevProps.race.id === nextProps.race.id &&
    prevProps.race.status?.code === nextProps.race.status?.code &&
    isEqual(prevProps.race.mtp, nextProps.race.mtp) &&
    isEqual(prevProps.race.bettingInterests, nextProps.race.bettingInterests) &&
    isEqual(prevProps.races, nextProps.races) &&
    prevProps.isAuxiliaryHigherThanPrimary ===
      nextProps.isAuxiliaryHigherThanPrimary &&
    prevProps.myBetsMaxHeight === nextProps.myBetsMaxHeight
);
